export type ShareChannel = "sms" | "whatsapp" | "email";

/**
 * Where a prewritten message goes when the student picks a channel.
 *
 * These are handoffs to apps the student already has, not sends of our own.
 * Nothing leaves the device until they press send in the app that opens, which
 * is the point: the message names their exam scores and goes to their parent.
 */
export function shareUrl(input: {
  channel: ShareChannel;
  message: string;
  subject: string;
  /** For WhatsApp/SMS: pre-fill the recipient. Digits only (no +). */
  recipient?: string | undefined;
}): string {
  const body = encodeURIComponent(input.message);
  const to = input.recipient?.replace(/\D/g, "") ?? "";

  switch (input.channel) {
    case "whatsapp":
      /*
       * Use the intent scheme (`whatsapp://send`) rather than `wa.me/?text=`.
       * The wa.me form silently drops text surrounding an embedded URL on some
       * devices (especially iOS), showing only the link as a preview card.
       * The intent scheme reliably preserves the full message.
       *
       * When a recipient is known, include `phone=<number>` so WhatsApp
       * pre-selects the chat.
       */
      return to
        ? `https://api.whatsapp.com/send?phone=${to}&text=${body}`
        : `https://api.whatsapp.com/send?text=${body}`;

    case "email":
      return `mailto:?subject=${encodeURIComponent(input.subject)}&body=${body}`;

    case "sms":
    default:
      /*
       * `sms:?&body=` rather than one or the other.
       *
       * Android wants `?body=` and iOS wants `&body=` when there is no
       * recipient; the combined form is the one both accept, and getting it
       * wrong opens the messaging app with an empty draft, which looks like
       * the message was lost.
       */
      return to ? `sms:${to}?&body=${body}` : `sms:?&body=${body}`;
  }
}
